import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { Job } from "../../../models";
import {
	MarkJobReadyFormInputs,
	MarkJobReadyFormProps,
	DEFAULT_FORM_VALUES,
	MarkJobReadyFormViewModel,
} from "./MarkJobReadyForm.types";
import { debounce } from "../../../utilities/utils";

/**
 * Custom hook that encapsulates form state management and business logic
 *
 * @param props - Component props including jobs, callbacks, and configuration
 * @returns ViewModel with all data and handlers for the View
 */
export function useMarkJobReadyFormViewModel(props: MarkJobReadyFormProps): MarkJobReadyFormViewModel {
	const { jobs: propsJobs, initialSelectedJobs, onSubmit, onSelectionChange, showOnlySelectedJobs = false } = props;

	// Determine which jobs to display based on mode
	const displayedJobs = useMemo(() => {
		if (showOnlySelectedJobs && initialSelectedJobs && initialSelectedJobs.length > 0) {
			// Filter mode (for popover): only show jobs from initialSelectedJobs
			const selectedJobIds = new Set(initialSelectedJobs.filter((job): job is Job => !!job).map((job) => job.id));
			return propsJobs.filter((job) => selectedJobIds.has(job.id));
		}
		// Default mode (for page): show all jobs
		return propsJobs;
	}, [propsJobs, initialSelectedJobs, showOnlySelectedJobs]);

	// Local state for jobs list (synced from displayed jobs)
	const [jobs, setJobs] = useState<Job[]>(displayedJobs);

	// Sync jobs when displayed jobs change
	useEffect(() => {
		setJobs(displayedJobs);
	}, [displayedJobs]);

	// Initialize react-hook-form
	const {
		register,
		watch,
		handleSubmit,
		control,
		reset,
		formState: { errors },
	} = useForm<MarkJobReadyFormInputs>({
		defaultValues: {
			...DEFAULT_FORM_VALUES,
			// Filter out any undefined/null values from initialSelectedJobs
			selectedJobs: initialSelectedJobs.filter((job): job is Job => !!job),
		},
	});

	// Watch form changes and notify parent with debouncing
	useEffect(() => {
		// Create debounced callback (300ms delay for text inputs)
		const debouncedCallback = debounce((selectedJobs: Job[]) => {
			if (onSelectionChange) {
				onSelectionChange(selectedJobs);
			}
		}, 300);

		// Subscribe to form changes
		const subscription = watch((value) => {
			if (value.selectedJobs) {
				// Filter out any undefined values before passing to callback
				const validJobs = value.selectedJobs.filter((job): job is Job => !!job);
				debouncedCallback(validJobs);
			}
		});

		// Cleanup: unsubscribe and cancel pending debounced calls
		return () => {
			subscription.unsubscribe();
			debouncedCallback.cancel();
		};
	}, [watch, onSelectionChange]);

	// Computed value: count of selected jobs
	const jobsSelected = watch("selectedJobs")?.length || 0;

	// Wrapped submit handler that includes form reset
	const handleFormSubmit = handleSubmit((data) => {
		onSubmit(data);
		reset();
	});

	return {
		control,
		register,
		watch,
		errors,
		jobs,
		jobsSelected,
		handleFormSubmit,
	};
}
